import React, { memo, useCallback } from 'react';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { TFunction } from 'i18next';
import { useFormContext } from 'react-hook-form';
import _get from 'lodash/get';
import classNames from 'classnames';
import { FieldComponents, FieldItem, Values } from '@lib/interfaces/form';
import removeIdFields from '@lib/utils/removeIdsFromObject';
import getLastPosition from '@lib/utils/getLastPosition';
import FormItem from './FormItem';
import FieldArrayItemAccordion from './FieldArrayItemAccordion';
// eslint-disable-next-line import/no-cycle
import SubFieldsArray from './FieldArray'; // TODO: fix it
import styles from './Form.module.scss';

export interface ItemProps {
  append: (v: Values) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  emptyStateText?: string;
  emptyStateTitle?: string;
  entity?: string;
  fieldComponents?: FieldComponents;
  formId?: string;
  formItemsClassName?: string;
  groupIndex?: number;
  id?: string;
  index: number;
  isDraggable?: boolean;
  isFocused?: boolean;
  isSubItemDisabled?: boolean;
  isSubItemDraggable?: boolean;
  isSubItemRemovable?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onNextFocus?: () => void;
  onPrevFocus?: () => void;
  parentName: string;
  remove: (index: number) => void;
  sourceAppendValues?: { [key: string]: unknown };
  subFields: FieldItem[];
  subFieldsMaxLength?: number;
  subFieldsMinLength?: number;
  t: TFunction<'translation', undefined>;
  title?: string;
}

function FieldArrayItem(props: ItemProps) {
  const {
    append,
    dragHandleProps,
    fieldComponents,
    formId,
    formItemsClassName,
    groupIndex,
    id,
    index,
    isDraggable,
    isFocused,
    isSubItemDisabled = false,
    isSubItemDraggable = true,
    isSubItemRemovable = true,
    onClick,
    onNextFocus,
    onPrevFocus,
    parentName,
    remove,
    sourceAppendValues,
    subFields,
    subFieldsMaxLength,
    subFieldsMinLength,
    title,
  } = props;
  const { formState, watch, resetField, setValue } = useFormContext();
  const errors = _get(formState, ['errors', parentName, index]);
  const errorsCount = errors ? Object.keys(errors).length : 0;
  const values = watch();
  const parentValue = _get(values, parentName, []);
  const value = parentValue?.[index];

  const sourceAppendValue = sourceAppendValues?.[parentName] as
    Values | undefined;

  const onAdd = useCallback(
    (appendValue: Values, appendPosition?: number) => {
      const lastPosition = getLastPosition(parentValue);
      const preparedAppendValue = appendValue || undefined;

      if (preparedAppendValue && !preparedAppendValue.position) {
        preparedAppendValue.position = lastPosition + 1;
      }

      if (typeof appendPosition === 'number' && appendPosition >= 0) {
        const newArray = [
          ...parentValue.slice(0, appendPosition),
          preparedAppendValue,
          ...parentValue.slice(appendPosition),
        ];
        setValue(parentName, newArray, {
          shouldDirty: true,
          shouldValidate: true,
        });
        if (onNextFocus) onNextFocus();
        return undefined;
      }

      return sourceAppendValue || preparedAppendValue
        ? append(preparedAppendValue)
        : undefined;
    },
    [parentValue, sourceAppendValue, append, setValue, parentName, onNextFocus],
  );

  const onDuplicate = useCallback(
    () => append(removeIdFields(value)),
    [append, value],
  );

  const onRemove = useCallback(() => {
    remove(index);
    resetField(parentName, {
      defaultValue: parentValue.filter((_, idx) => idx !== index),
    });
    if (onPrevFocus) onPrevFocus();
  }, [index, onPrevFocus, parentName, parentValue, remove, resetField]);

  const getSubFieldContent = () => (
    <div className={classNames(styles.formSubItems, formItemsClassName)}>
      {subFields.map((subField) => {
        const {
          name,
          validation,
          getLabel,
          subFields: subSubFields,
          componentProps,
          ...restFieldProps
        } = subField;
        const { label } = restFieldProps;
        let newLabel = label;
        const fieldName = `${parentName}.${index}.${name}`;
        if (getLabel) {
          newLabel = getLabel(values, fieldName);
        }
        if (subSubFields !== undefined) {
          return (
            <SubFieldsArray
              parentName={fieldName}
              subFields={subSubFields}
              key={`${fieldName}-${id}`}
              formId={formId}
              sourceAppendValues={sourceAppendValues}
              {...subField}
            />
          );
        }
        return (
          <FormItem
            {...restFieldProps}
            componentProps={{
              ...componentProps,
              disabled: isSubItemDisabled || componentProps?.disabled,
            }}
            key={`${fieldName}-${id}`}
            name={fieldName}
            label={newLabel}
            formId={formId}
            validation={validation}
          />
        );
      })}
    </div>
  );

  if (fieldComponents?.FieldArrayItemWrapperComponent) {
    const { FieldArrayItemWrapperComponent } = fieldComponents;
    return (
      <FieldArrayItemWrapperComponent
        dragHandleProps={dragHandleProps}
        errorsCount={errorsCount}
        isDraggable={isDraggable}
        isFocused={isFocused}
        isSubItemDisabled={isSubItemDisabled}
        isSubItemDraggable={isSubItemDraggable}
        isSubItemRemovable={isSubItemRemovable}
        onAdd={onAdd}
        onClick={onClick}
        onDuplicate={onDuplicate}
        onRemove={onRemove}
        parentGroupIndex={groupIndex}
        parentIndex={index}
        parentName={parentName}
        parentValue={parentValue}
        subFieldsMaxLength={subFieldsMaxLength}
        subFieldsMinLength={subFieldsMinLength}
        value={value}
      >
        {getSubFieldContent()}
      </FieldArrayItemWrapperComponent>
    );
  }

  return (
    <FieldArrayItemAccordion
      dragHandleProps={dragHandleProps}
      errorsCount={errorsCount}
      index={index}
      isDraggable={isDraggable}
      onRemove={() => remove(index)}
      parentName={parentName}
      title={`${title} ${index + 1}`}
    >
      {getSubFieldContent()}
    </FieldArrayItemAccordion>
  );
}

export default memo(FieldArrayItem);
